/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Package, BookOpen, Layers, LogOut, Cloud, Laptop, User, Users, ShoppingBag, Globe, Calendar, Trophy } from 'lucide-react';
import { motion } from 'motion/react';
import { ALL_STICKERS } from '../constants';
import { Avatar } from './Avatar';
import { LANGUAGES, LanguageCode, TRANSLATIONS } from '../translations';

interface NavProps {
    view: string;
    setView: (v: any) => void;
    packs: number;
    setPacks: (v: any) => void;
    doOpen: () => void;
    owned: Set<string>;
    user?: any;
    onLogOut?: () => void;
    giftCount?: number;
    completedTradesCount?: number;
    lang: LanguageCode;
    setLang: (l: LanguageCode) => void;
}

export const Navigation = ({
                               view,
                               setView,
                               packs,
                               setPacks,
                               doOpen,
                               owned,
                               user,
                               onLogOut,
                               giftCount = 0,
                               completedTradesCount = 0,
                               lang,
                               setLang,
                           }: NavProps) => {
    const pct = Math.round((owned.size / ALL_STICKERS.length) * 100);
    const t = (key: string) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS['en']?.[key] || key;

    return (
        <>
            <nav className="bg-[#1e293b] border-b border-slate-700 px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-40 shadow-lg">
                <div className="flex items-center gap-1.5 sm:gap-4">
                    <button
                        onClick={() => setView('cover')}
                        className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 font-mono text-sm shrink-0"
                    >
                        P
                    </button>
                    <div className="flex flex-col min-w-0">
                        <span className="text-xs sm:text-sm font-semibold text-white tracking-tight truncate">{t('appTitle')}</span>
                        <span className="text-[8px] sm:text-[9px] text-slate-400 uppercase tracking-widest font-mono truncate">{t('appSubtitle')}</span>
                    </div>

                    {/* Custom Inline Language Selector */}
                    <div className="relative inline-flex items-center gap-1 ml-1 sm:ml-2">
                        <Globe size={12} className="text-slate-400 shrink-0" />
                        <select
                            value={lang}
                            onChange={(e) => setLang(e.target.value as LanguageCode)}
                            className="bg-[#0f172a] text-slate-300 text-[9px] sm:text-[10px] font-medium rounded border border-slate-700 py-1 pl-1 pr-4 sm:pr-6 cursor-pointer hover:bg-slate-900 transition-colors focus:outline-none appearance-none font-sans"
                            style={{
                                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='rgb(148, 163, 184)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 4px center',
                                backgroundSize: '8px'
                            }}
                        >
                            {LANGUAGES.map((l) => (
                                <option key={l.code} value={l.code} className="bg-[#0f172a] text-white">
                                    {l.flag} {l.code.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-4">
                    {/* Sync Status Badge */}
                    <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-[#0f172a] rounded border border-slate-800 mr-2">
                        {user ? (
                            <div className="flex items-center gap-2">
                                <span className="text-[14px]">⛓️</span>
                                <div className="flex flex-col text-left">
                                    <span className="text-[8px] font-mono text-slate-500 uppercase leading-none">{t('accountStorage')}</span>
                                    <span className="text-[10px] font-mono text-emerald-400 leading-none">{t('onlineSynced')}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="text-[14px]">📡</span>
                                <div className="flex flex-col text-left">
                                    <span className="text-[8px] font-mono text-slate-500 uppercase leading-none">{t('offlineSession')}</span>
                                    <span className="text-[10px] font-mono text-yellow-500 leading-none">{t('savedLocally')}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Desktop Navigation Items */}
                    {user && view !== 'friends' && (
                        <button
                            onClick={() => setView('friends')}
                            className="hidden md:flex items-center gap-1.5 text-slate-300 hover:text-blue-400 text-xs font-medium transition-colors px-2 py-1 rounded hover:bg-slate-800 relative"
                        >
                            <div className="relative">
                                <Users size={14} />
                                {completedTradesCount > 0 && (
                                    <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 border border-[#1e293b]"></span>
                  </span>
                                )}
                            </div>
                            <span>{t('friends')}</span>
                            {completedTradesCount > 0 && (
                                <span className="bg-emerald-500 text-slate-950 font-bold font-mono text-[7px] px-1 rounded-sm uppercase tracking-wider animate-pulse ml-0.5">
                  SWAP
                </span>
                            )}
                        </button>
                    )}

                    {view !== 'album' && (
                        <button
                            onClick={() => setView('album')}
                            className="hidden md:flex items-center gap-1.5 text-slate-300 hover:text-blue-400 text-xs font-medium transition-colors px-2 py-1 rounded hover:bg-slate-800"
                        >
                            <BookOpen size={14} />
                            <span>{t('album')}</span>
                        </button>
                    )}

                    {view !== 'groups' && (
                        <button
                            onClick={() => setView('groups')}
                            className="hidden md:flex items-center gap-1.5 text-slate-300 hover:text-blue-400 text-xs font-medium transition-colors px-2 py-1 rounded hover:bg-slate-800"
                        >
                            <Layers size={14} />
                            <span>{t('groups')}</span>
                        </button>
                    )}

                    {view !== 'schedules' && (
                        <button
                            onClick={() => setView('schedules')}
                            className="hidden md:flex items-center gap-1.5 text-slate-300 hover:text-blue-400 text-xs font-medium transition-colors px-2 py-1 rounded hover:bg-slate-800"
                        >
                            <Calendar size={14} className="text-amber-400" />
                            <span>{t('schedules') || 'Schedules'}</span>
                        </button>
                    )}

                    {view !== 'quiz' && (
                        <button
                            onClick={() => setView('quiz')}
                            className="hidden md:flex items-center gap-1.5 text-slate-300 hover:text-blue-400 text-xs font-medium transition-colors px-2 py-1 rounded hover:bg-slate-800"
                            id="desktop-quiz-nav-btn"
                        >
                            <Trophy size={14} className="text-amber-400 animate-pulse" />
                            <span>{t('quiz') || 'Quiz'}</span>
                        </button>
                    )}

                    <button
                        onClick={() => setView('shop')}
                        className={`hidden md:flex items-center gap-1.5 text-slate-300 hover:text-blue-400 text-xs font-medium transition-colors px-2 py-1 rounded hover:bg-slate-800 relative ${
                            view === 'shop' ? 'text-blue-400 bg-[#0f172a] border border-slate-800/60 shadow-inner' : ''
                        }`}
                    >
                        <div className="relative">
                            <ShoppingBag size={14} />
                            {packs === 0 && (
                                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500"></span>
                </span>
                            )}
                        </div>
                        <span>{t('shop')}</span>
                        {packs === 0 && (
                            <span className="bg-blue-500 text-white font-bold font-mono text-[7px] px-1 rounded-sm uppercase tracking-wider ml-0.5 animate-pulse">
                GET
              </span>
                        )}
                    </button>

                    {/* Quick Open Pack Button — Always visible but compacted on mobile */}
                    <button
                        onClick={doOpen}
                        disabled={packs <= 0}
                        className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-all shadow-md active:scale-95 ${
                            packs > 0
                                ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-blue-500/20 cursor-pointer animate-pulse'
                                : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                        }`}
                    >
                        <Package size={14} />
                        <span className="hidden sm:inline">{t('pack')}</span> <span>({packs})</span>
                    </button>

                    {/* User Info & Out Option */}
                    {user ? (
                        <div className="flex items-center gap-1.5 sm:gap-2 pl-2 border-l border-slate-700">
                            <button
                                onClick={() => setView('profile')}
                                title="View User Profile"
                                className="flex items-center gap-1.5 sm:gap-2 hover:text-white focus:outline-none transition-all cursor-pointer text-left relative"
                            >
                                <div className="relative">
                                    <Avatar photoURL={user.photoURL} email={user.email} size="sm" />
                                    {giftCount > 0 && (
                                        <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500 border border-[#1e293b]"></span>
                    </span>
                                    )}
                                </div>
                                <div className="flex flex-col min-w-0">
                  <span className="hidden xl:inline text-[10px] font-mono text-slate-400 max-w-[80px] truncate hover:text-white transition-colors font-bold">
                    {user.displayName || user.email.split('@')[0]}
                  </span>
                                    {giftCount > 0 && (
                                        <span className="text-[8px] font-mono text-amber-400 font-bold tracking-wider uppercase animate-pulse leading-none mt-0.5">
                      🎁 GIFT
                    </span>
                                    )}
                                </div>
                            </button>
                            <button
                                onClick={onLogOut}
                                title={t('signOut')}
                                className="p-1 text-slate-500 hover:text-red-400 rounded hover:bg-slate-800 transition-colors"
                            >
                                <LogOut size={14} />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setView('cover')}
                            className="flex items-center gap-1 px-2.5 py-1 text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded text-xs transition-colors font-mono uppercase tracking-wider text-[10px] shrink-0"
                        >
                            <User size={12} />
                            <span className="hidden sm:inline">{t('login')}</span>
                        </button>
                    )}

                    {/* Progress Bar (Desktop-only to save horizontal layout) */}
                    <div className="hidden lg:flex flex-col items-end min-w-[70px] sm:min-w-[80px]">
                        <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[9px] font-mono font-bold text-slate-500 uppercase tracking-tighter">
                {t('progress')}: {pct}%
              </span>
                        </div>
                        <div className="w-16 sm:w-20 h-1 bg-slate-800 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${pct}%` }}
                                className="h-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                            />
                        </div>
                    </div>
                </div>
            </nav>

            {/* Floating Sticky Bottom Mobile Tab Bar (Visible only on md screens and below) */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-[#131d30] border-t border-slate-800/80 px-1.5 flex items-center justify-around z-40 shadow-[0_-8px_30px_rgba(0,0,0,0.6)] pb-safe backdrop-blur-md">
                {/* Album Tab */}
                <button
                    onClick={() => setView('album')}
                    className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 active:scale-95 ${
                        view === 'album' || view === 'team' ? 'text-blue-500 font-bold' : 'text-slate-400 hover:text-slate-300'
                    }`}
                >
                    <BookOpen size={18} className={view === 'album' || view === 'team' ? 'text-blue-500' : 'text-slate-400'} />
                    <span className="text-[9px] mt-1 tracking-tight font-sans truncate w-full text-center">
            {t('album')}
          </span>
                </button>

                {/* Groups Tab */}
                <button
                    onClick={() => setView('groups')}
                    className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 active:scale-95 ${
                        view === 'groups' ? 'text-blue-500 font-bold' : 'text-slate-400 hover:text-slate-300'
                    }`}
                >
                    <Layers size={18} className={view === 'groups' ? 'text-blue-500' : 'text-slate-400'} />
                    <span className="text-[9px] mt-1 tracking-tight font-sans truncate w-full text-center">
            {t('groups')}
          </span>
                </button>

                {/* Schedules Tab */}
                <button
                    onClick={() => setView('schedules')}
                    className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 active:scale-95 ${
                        view === 'schedules' ? 'text-blue-500 font-bold' : 'text-slate-400 hover:text-slate-300'
                    }`}
                >
                    <Calendar size={18} className={view === 'schedules' ? 'text-blue-500' : 'text-slate-400'} />
                    <span className="text-[9px] mt-1 tracking-tight font-sans truncate w-full text-center">
            {t('schedules')}
          </span>
                </button>

                {/* Quiz Tab */}
                <button
                    onClick={() => setView('quiz')}
                    className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 active:scale-95 ${
                        view === 'quiz' ? 'text-blue-500 font-bold' : 'text-slate-400 hover:text-slate-300'
                    }`}
                    id="mobile-quiz-nav-btn"
                >
                    <Trophy size={18} className={view === 'quiz' ? 'text-blue-500 hover:text-blue-500' : 'text-slate-400'} />
                    <span className="text-[9px] mt-1 tracking-tight font-sans truncate w-full text-center">
            {t('quiz') || 'Quiz'}
          </span>
                </button>

                {/* Shop Tab */}
                <button
                    onClick={() => setView('shop')}
                    className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 active:scale-95 relative ${
                        view === 'shop' ? 'text-blue-500 font-bold' : 'text-slate-400 hover:text-slate-300'
                    }`}
                >
                    <div className="relative">
                        <ShoppingBag size={18} className={view === 'shop' ? 'text-blue-500' : 'text-slate-400'} />
                        {packs === 0 && (
                            <span className="absolute -top-1 -right-1 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500"></span>
              </span>
                        )}
                    </div>
                    <span className="text-[9px] mt-1 tracking-tight font-sans truncate w-full text-center">
            {t('shop')}
          </span>
                </button>

                {/* Friends / Social Tab */}
                <button
                    onClick={() => setView(user ? 'friends' : 'cover')}
                    className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 active:scale-95 relative ${
                        view === 'friends' ? 'text-blue-500 font-bold' : 'text-slate-400 hover:text-slate-300'
                    }`}
                >
                    <div className="relative">
                        <Users size={18} className={view === 'friends' ? 'text-blue-500' : 'text-slate-400'} />
                        {completedTradesCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-20 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
              </span>
                        )}
                    </div>
                    <span className="text-[9px] mt-1 tracking-tight font-sans truncate w-full text-center">
            {t('friends')}
          </span>
                </button>
            </div>
        </>
    );
};
